// Écran principal : la liste des dossiers, et la conversation de celui ouvert.
//
// Tout ce qui s'affiche ici a été déchiffré dans ce navigateur. Le serveur n'a
// relayé que des blobs opaques.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../auth/store';
import { DossierAudit } from '../audit/AuditPanel';
import { Calendar } from './Calendar';
import { Documents } from './Documents';
import {
  addMissingOwnDevices,
  createDossier,
  decryptDossierDetails,
  groupEpoch,
  groupMembers,
  inviteParticipant,
  listDossiers,
  listParticipants,
  lookupUser,
  roleForUserType,
  sendMessage,
  syncDossier,
  topUpKeyPackages,
  type Ctx,
  type Dossier,
  type DossierDetails,
  type Participant,
  type SyncResult,
} from './engine';

const AFFAIRE_TYPES = [
  'conseil',
  'contentieux',
  'acte',
  'divorce',
  'penal',
  'commercial',
  'famille',
  'autre',
];

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

export function DossiersPage() {
  const session = useAuth((s) => s.session);
  const me = useAuth((s) => s.me);
  const masterKey = useAuth((s) => s.masterKey);
  const device = useAuth((s) => s.device);

  const ctx: Ctx | null = useMemo(() => {
    if (!session || !me || !masterKey || !device) return null;
    return {
      token: session.sessionToken,
      masterKey: masterKey.masterKey,
      deviceId: device.deviceId,
      userId: me.userId,
      client: device.client,
      isPrimary: device.isPrimary,
    };
  }, [session, me, masterKey, device]);

  const [dossiers, setDossiers] = useState<Dossier[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Au démarrage : reconstituer le stock de KeyPackages (sans lui, personne ne
  // pourrait nous inviter dans un dossier), puis lister les dossiers.
  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;

    (async () => {
      try {
        await topUpKeyPackages(ctx);
        const list = await listDossiers(ctx);
        if (cancelled) return;
        setDossiers(list);
        setOpenId((current) => current ?? list[0]?.id ?? null);
      } catch (e) {
        if (!cancelled) setError(message(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ctx]);

  if (!ctx) return null;

  async function onCreate(details: DossierDetails) {
    setError(null);
    try {
      const dossier = await createDossier(ctx!, details);
      setDossiers((d) => [dossier, ...d]);
      setOpenId(dossier.id);
    } catch (e) {
      setError(message(e));
    }
  }

  const open = dossiers.find((d) => d.id === openId) ?? null;

  return (
    <section className="mt-10 text-left" data-testid="dossiers-page">
      {error && (
        <p data-testid="dossiers-error" className="mb-4 text-sm text-red-600">
          {error}
        </p>
      )}

      <div className="grid gap-8 md:grid-cols-[16rem_1fr]">
        <aside>
          <h2 className="text-sm font-semibold text-slate-900">Dossiers</h2>

          {loading ? (
            <p className="mt-2 text-xs text-slate-400">Chargement…</p>
          ) : dossiers.length === 0 ? (
            <p className="mt-2 text-xs text-slate-400">Aucun dossier.</p>
          ) : (
            <ul className="mt-3 space-y-1" data-testid="dossier-list">
              {dossiers.map((d) => (
                <li key={d.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(d.id)}
                    className={`w-full rounded px-3 py-2 text-left text-sm ${
                      d.id === openId
                        ? 'bg-blue-900 text-white'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {/* Tout est déchiffré ici. Sans la clé du dossier, il ne reste
                        RIEN à afficher — et c'est exactement ce que le serveur
                        voit, lui, pour toujours. */}
                    <span className="block font-medium">
                      {d.details?.name ?? (
                        <em className="font-normal opacity-70">
                          dossier en attente de clé
                        </em>
                      )}
                    </span>
                    <span
                      className={`block text-xs ${d.id === openId ? 'text-blue-200' : 'text-slate-400'}`}
                    >
                      {d.details
                        ? `${d.details.reference} · ${d.details.affaireType}`
                        : 'ouvre-le pour rejoindre le groupe'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <NewDossier onCreate={onCreate} />
        </aside>

        {open ? (
          <DossierThread
            key={open.id}
            ctx={ctx}
            dossier={open}
            // La clé du dossier n'arrive qu'à la synchronisation (par le groupe
            // MLS). Les détails ne sont donc déchiffrables qu'après — on les
            // remonte ici pour que la liste s'actualise.
            onDetails={(id, details) =>
              setDossiers((ds) =>
                ds.map((d) => (d.id === id ? { ...d, details } : d)),
              )
            }
          />
        ) : (
          <p className="text-sm text-slate-400">
            Sélectionne un dossier, ou crée-en un.
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------

function NewDossier({
  onCreate,
}: {
  onCreate: (details: DossierDetails) => Promise<void>;
}) {
  const [reference, setReference] = useState('');
  const [name, setName] = useState('');
  const [affaireType, setAffaireType] = useState('conseil');
  const [busy, setBusy] = useState(false);

  const pret = reference.trim() && name.trim();

  return (
    <form
      className="mt-6 border-t border-slate-200 pt-4"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!pret) return;
        setBusy(true);
        await onCreate({
          reference: reference.trim(),
          name: name.trim(),
          affaireType,
        });
        setReference('');
        setName('');
        setBusy(false);
      }}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Nouveau dossier
      </h3>

      {/* La référence est chiffrée elle aussi : dans un vrai cabinet, elle porte
          le nom du client (« DUPONT-2026-04 »). */}
      <input
        className="mt-2 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
        placeholder="Référence (ex. 2026-014)"
        value={reference}
        onChange={(e) => setReference(e.target.value)}
        data-testid="dossier-reference"
      />

      <input
        className="mt-2 w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
        placeholder="Intitulé (ex. Divorce Dupont c/ Dupont)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        data-testid="dossier-name"
      />

      <select
        className="mt-2 w-full rounded border border-slate-300 px-3 py-2 text-sm"
        value={affaireType}
        onChange={(e) => setAffaireType(e.target.value)}
        data-testid="dossier-type"
      >
        {AFFAIRE_TYPES.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </select>
      <button
        type="submit"
        disabled={busy || !pret}
        data-testid="create-dossier"
        className="mt-2 w-full rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
      >
        {busy ? 'Création…' : 'Créer'}
      </button>
      <p className="mt-2 text-[10px] text-slate-400">
        Intitulé, référence et nature de l&apos;affaire sont chiffrés pour les
        membres du dossier. Le serveur ne saura que ceci : un dossier existe,
        ouvert aujourd&apos;hui.
      </p>
    </form>
  );
}

// ---------------------------------------------------------------------------

function DossierThread({
  ctx,
  dossier,
  onDetails,
}: {
  ctx: Ctx;
  dossier: Dossier;
  onDetails: (id: string, details: DossierDetails) => void;
}) {
  const [sync, setSync] = useState<SyncResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  // Les participants servent deux fois : le panneau « Participants », et
  // l'attribution des messages (authorId → e-mail). Rechargés à chaque
  // évolution du groupe.
  const [members, setMembers] = useState<Participant[] | null>(null);
  const [membersError, setMembersError] = useState<string | null>(null);
  const epoch = sync ? groupEpoch(sync.state).toString() : null;

  useEffect(() => {
    if (epoch === null) return;
    let cancelled = false;
    listParticipants(ctx, dossier.id)
      .then((list) => {
        if (!cancelled) setMembers(list);
      })
      .catch((e) => {
        if (!cancelled) setMembersError(message(e));
      });
    return () => {
      cancelled = true;
    };
  }, [ctx, dossier.id, epoch]);

  const memberById = useMemo(
    () => new Map((members ?? []).map((m) => [m.userId, m])),
    [members],
  );

  const refresh = useCallback(async () => {
    setError(null);
    try {
      let result = await syncDossier(ctx, dossier.id);

      // Un appareil secondaire fraîchement approuvé est une NOUVELLE feuille MLS :
      // tant qu'il n'a pas été ajouté au groupe, il ne déchiffre rien. Seul un
      // membre peut l'y ajouter — donc l'appareil principal, ici.
      if (ctx.isPrimary && result.joined) {
        result = await addMissingOwnDevices(ctx, dossier.id, result);
      }
      setSync(result);

      // La synchronisation a pu nous apporter la clé du dossier : les détails
      // (intitulé, référence, nature) deviennent alors lisibles.
      if (!dossier.details) {
        const details = decryptDossierDetails(
          result.state.dossierKey,
          dossier.encryptedDetails,
        );
        if (details) onDetails(dossier.id, details);
      }
    } catch (e) {
      setError(message(e));
    }
  }, [ctx, dossier.id, dossier.details, dossier.encryptedDetails, onDetails]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function onSend(e: React.FormEvent) {
    e.preventDefault();
    if (!sync?.joined || !draft.trim()) return;
    setBusy(true);
    setError(null);
    try {
      setSync(
        await sendMessage(ctx, dossier.id, sync.state, sync.revision, draft.trim()),
      );
      setDraft('');
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  if (!sync) {
    return <p className="text-sm text-slate-400">Déchiffrement du dossier…</p>;
  }

  if (!sync.joined) {
    return (
      <div data-testid="not-member">
        <h2 className="text-lg font-semibold text-slate-900">
          {dossier.details?.reference ?? 'Dossier en attente de clé'}
        </h2>
        <p className="mt-2 text-sm text-amber-700">
          Tu as accès à ce dossier, mais cet appareil n&apos;a pas encore rejoint
          le groupe chiffré. Il le rejoindra dès que ton appareil principal
          l&apos;y aura ajouté — ouvre Delo dessus.
        </p>
        <button
          type="button"
          onClick={refresh}
          data-testid="refresh"
          className="mt-3 rounded border border-slate-300 px-3 py-2 text-xs text-slate-700 hover:bg-slate-50"
        >
          Actualiser
        </button>
      </div>
    );
  }

  return (
    <div>
      <header className="flex items-baseline gap-3 border-b border-slate-200 pb-3">
        <h2 className="text-lg font-semibold text-slate-900" data-testid="dossier-title">
          {dossier.details?.name ?? 'Dossier en attente de clé'}
        </h2>
        {dossier.details && (
          <span className="text-xs text-slate-400">
            {dossier.details.reference}
          </span>
        )}
        <span className="text-xs text-slate-400" data-testid="group-info">
          {groupMembers(sync.state)} membre(s) · epoch{' '}
          {groupEpoch(sync.state).toString()}
        </span>
        <button
          type="button"
          onClick={refresh}
          className="ml-auto text-xs text-slate-500 underline hover:text-slate-700"
          data-testid="refresh"
        >
          Actualiser
        </button>
      </header>

      {error && (
        <p data-testid="thread-error" className="mt-3 text-sm text-red-600">
          {error}
        </p>
      )}

      <Members me={ctx.userId} members={members} error={membersError} />

      <ul className="mt-4 space-y-3" data-testid="messages">
        {sync.state.messages.length === 0 && (
          <li className="text-sm text-slate-400">Aucun message.</li>
        )}
        {sync.state.messages.map((m) => {
          const mine = m.authorId === ctx.userId;
          // L'auteur a pu quitter le dossier depuis : son message reste, son
          // e-mail n'est plus dans la liste des participants.
          const author = memberById.get(m.authorId)?.email ?? 'ancien membre';
          return (
            <li key={m.id} className={mine ? 'text-right' : 'text-left'}>
              {!mine && (
                <p
                  className="mb-0.5 text-[10px] font-medium text-slate-500"
                  data-testid="message-author"
                >
                  {author}
                </p>
              )}
              <div
                className={`inline-block max-w-[80%] rounded px-3 py-2 text-sm ${
                  mine ? 'bg-blue-900 text-white' : 'bg-slate-100 text-slate-800'
                }`}
              >
                {m.text ?? (
                  <em className={mine ? 'text-blue-200' : 'text-slate-400'}>
                    contenu indisponible sur cet appareil
                  </em>
                )}
              </div>
              <p className="mt-1 text-[10px] text-slate-400">
                {new Date(m.at).toLocaleString('fr-FR')}
              </p>
            </li>
          );
        })}
      </ul>

      <form onSubmit={onSend} className="mt-6 flex gap-2">
        <input
          className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
          placeholder="Message chiffré de bout en bout…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="message-input"
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          data-testid="send-message"
          className="rounded bg-blue-900 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {busy ? 'Envoi…' : 'Envoyer'}
        </button>
      </form>

      {/* Les échéances avant les pièces : manquer un délai de prescription est
          une faute disciplinaire — c'est ce qu'un avocat doit voir en premier. */}
      <Calendar ctx={ctx} dossierId={dossier.id} state={sync.state} />

      <Documents ctx={ctx} dossierId={dossier.id} state={sync.state} />

      {/* Le journal du dossier. Réservé à son gestionnaire côté serveur — pour
          les autres, l'appel échoue et le panneau affiche pourquoi. */}
      <DossierAudit dossierId={dossier.id} />

      <InviteForm
        ctx={ctx}
        dossierId={dossier.id}
        sync={sync}
        onInvited={setSync}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

const ROLE_LABELS: Record<string, string> = {
  avocat: 'avocat',
  collaborateur: 'collaborateur',
  client: 'client',
  tiers: 'intervenant extérieur',
};

function Members({
  me,
  members,
  error,
}: {
  me: string;
  members: Participant[] | null;
  error: string | null;
}) {
  if (error) {
    return (
      <p data-testid="members-error" className="mt-3 text-xs text-red-600">
        Participants : {error}
      </p>
    );
  }
  if (!members) return null;

  return (
    <div className="mt-3" data-testid="members">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Participants
      </h3>
      <ul className="mt-1 flex flex-wrap gap-2">
        {members.map((m) => (
          <li
            key={m.id}
            data-testid="member"
            className="flex items-baseline gap-2 rounded border border-slate-200 bg-slate-50 px-2 py-1"
          >
            <span className="text-xs font-medium text-slate-800">
              {m.email}
              {m.userId === me && ' (moi)'}
            </span>
            <span className="text-[10px] text-slate-500">
              {ROLE_LABELS[m.role] ?? m.role}
              {m.permissions.includes('admin_dossier') && ' · gestionnaire'}
              {m.validUntil &&
                ` · jusqu'au ${new Date(m.validUntil).toLocaleDateString('fr-FR')}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function InviteForm({
  ctx,
  dossierId,
  sync,
  onInvited,
}: {
  ctx: Ctx;
  dossierId: string;
  sync: SyncResult;
  onInvited: (s: SyncResult) => void;
}) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function onInvite(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const invitee = await lookupUser(ctx, email.trim());
      const next = await inviteParticipant(
        ctx,
        dossierId,
        sync.state,
        sync.revision,
        invitee,
        roleForUserType(invitee.userType),
        ['read', 'write', 'upload'],
      );
      onInvited(next);
      setEmail('');
      setDone(`${invitee.email} a rejoint le dossier chiffré.`);
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onInvite} className="mt-8 border-t border-slate-200 pt-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        Inviter au dossier
      </h3>
      <div className="mt-2 flex gap-2">
        <input
          className="flex-1 rounded border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
          placeholder="adresse e-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="invite-email"
        />
        <button
          type="submit"
          disabled={busy || !email.trim()}
          data-testid="invite-submit"
          className="rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {busy ? 'Invitation…' : 'Inviter'}
        </button>
      </div>

      {done && (
        <p data-testid="invite-done" className="mt-2 text-sm text-emerald-700">
          {done}
        </p>
      )}
      {error && (
        <p data-testid="invite-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}
    </form>
  );
}
